import axios from "axios";
import React, { useContext, useReducer, useState } from "react";
import { Form, Modal } from "react-bootstrap";
import Button from "react-bootstrap/Button";
import { Helmet } from "react-helmet-async";
import { useMediaQuery } from "react-responsive";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { Store } from "../Store";
import Gorsel from "../components/Gorsel";
import LoadingBox from "../components/LoadingBox";
import { default as config } from "../config.json";
import useSampleTableTypes from "../hooks/useSampleTableTypes";
import { getError } from "../utils";

const reducer = (state, action) => {
  switch (action.type) {
    case "CREATE_REQUEST":
      return { ...state, loadingCreate: true };
    case "CREATE_SUCCESS":
      return {
        ...state,
        loadingCreate: false,
      };
    case "CREATE_FAIL":
      return { ...state, loadingCreate: false };
    case "DELETE_REQUEST":
      return { ...state, loadingDelete: true, successDelete: false };
    case "DELETE_SUCCESS":
      return {
        ...state,
        loadingDelete: false,
        successDelete: true,
      };
    case "DELETE_FAIL":
      return { ...state, loadingDelete: false, successDelete: false };
    default:
      return state;
  }
};

export default function TableTypeSampleListScreen() {
  const [{ loadingDelete, loadingCreate }, dispatch] = useReducer(reducer, {
    loadingDelete: false,
    loadingCreate: false,
  });

  const { state } = useContext(Store);
  const { tableTypes = [], userInfo } = state; // SampleTableTypes from the Store
  const isTabletOrMobile = useMediaQuery({ query: "(max-width: 1224px)" });

  const [isEditMode, setIsEditMode] = useState(false);
  const [showTableTypeEditor, setShowTableTypeEditor] = useState(false);
  const [selectedId, setSelectedId] = useState("");

  const [image, setImage] = useState("");
  const [name, setName] = useState("");
  const [tableNumbers, setTableNumbers] = useState([]);
  const [, , setLoadSampleTableTypes] = useSampleTableTypes();

  const createOrUpdateHandler = async () => {
    try {
      dispatch({ type: "CREATE_REQUEST" });
      if (!isEditMode) {
        await axios.post(
          "/api/sampletabletypes",
          {
            name,
            image: image || config.default_image,
            tableNumbers,
          },
          {
            headers: {
              Authorization: `Bearer ${userInfo.token}`,
            },
          }
        );
      } else {
        await axios.put(
          `/api/sampletabletypes/${selectedId}`,
          {
            name,
            image: image || config.default_image,
            tableNumbers,
            _id: selectedId,
          },
          {
            headers: {
              Authorization: `Bearer ${userInfo.token}`,
            },
          }
        );
      }
      toast.success("Masa türü başarılı bir şekilde kaydedildi!");
      dispatch({ type: "CREATE_SUCCESS" });
      setName("");
      setImage("");
      setTableNumbers([]);
      setLoadSampleTableTypes(true);
      setShowTableTypeEditor(false);
    } catch (err) {
      toast.error(getError(err));
      dispatch({ type: "CREATE_FAIL" });
    }
  };

  const deleteHandler = async (tableType) => {
    if (window.confirm("Silmek istediğinize emin misiniz?")) {
      try {
        dispatch({ type: "DELETE_REQUEST" });

        await axios.delete(`/api/sampletabletypes/${tableType._id}`, {
          headers: {
            Authorization: `Bearer ${userInfo.token}`,
          },
        });
        setLoadSampleTableTypes(true);
        toast.success("Masa tipi başarıyla silindi.");
        dispatch({ type: "DELETE_SUCCESS" });
      } catch (err) {
        toast.error(getError(err));
        dispatch({ type: "DELETE_FAIL" });
      }
    }
  };

  const handleClickNewTableType = () => {
    setIsEditMode(false);
    setShowTableTypeEditor(true);
    setSelectedId("");
    setName("");
    setImage("");
    setTableNumbers([]);
  };

  const handleTableNumberChange = (e) => {
    const value = parseInt(e.target.value, 10);
    if (e.target.checked) {
      setTableNumbers((prev) => [...prev, value]);
    } else {
      setTableNumbers((prev) => prev.filter((num) => num !== value));
    }
  };

  return (
    <div className="small-container">
      <Helmet>
        <title>Masa Tipleri</title>
      </Helmet>
      {!isTabletOrMobile && (
        <>
          <h1 className="my-3 text-muted">Masa Tipleri</h1>
          <hr className="hr"></hr>
        </>
      )}
      <div className="hstack gap-3 mb-3">
        <div className="ms-auto">
          <Button
            type="button"
            onClick={handleClickNewTableType}
            className={"text-primary border-primary"}
            variant="light"
          >
            <i className="fas fa-plus"></i>
            {!isTabletOrMobile && <>&nbsp; Masa Tipi Oluştur</>}
          </Button>
        </div>
      </div>

      <div className="list-group">
        {loadingDelete || loadingCreate ? (
          <LoadingBox />
        ) : (
          tableTypes
            ?.filter((t) => t.status)
            .map((tableType) => (
              <div
                key={tableType._id}
                className="list-group-item d-flex justify-content-between align-items-center"
              >
                <div className="d-flex align-items-center">
                  <Link
                    className="textDecorationNone"
                    to={``}
                    onClick={() => {
                      setShowTableTypeEditor(true);
                      setIsEditMode(true);
                      setSelectedId(tableType._id);
                      setName(tableType.name);
                      setImage(tableType.image);
                      setTableNumbers(tableType.tableNumbers || []);
                    }}
                  >
                    <img
                      src={tableType.image}
                      alt={tableType.name}
                      className="me-3"
                      style={{ width: "50px", height: "50px" }}
                    />
                    <span>{tableType.name}</span>
                  </Link>
                </div>
                <div>
                  <Button
                    type="button"
                    className="border-danger text-danger"
                    variant="light"
                    onClick={() => deleteHandler(tableType)}
                  >
                    <i className="fas fa-trash-alt"></i>
                  </Button>
                </div>
              </div>
            ))
        )}
      </div>

      <Modal
        backdrop="static"
        show={showTableTypeEditor}
        onHide={() => {
          setShowTableTypeEditor(false);
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {isEditMode ? "Masa Tipini Düzenle" : "Yeni Masa Tipi"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Masa Tipi Adı</Form.Label>
            <Form.Control
              placeholder="Masa tipi adı"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </Form.Group>

          <Gorsel
            image={image}
            setImage={setImage}
            entityId={selectedId}
            entityName={"sampletabletype"}
          ></Gorsel>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-primary"
            onClick={createOrUpdateHandler}
            style={{ width: "100%" }}
          >
            Kaydet
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
